import { FIRST_PAGE } from '@/data/hook/shared/usePagination';
import usePendencyAdvancedFilters from '@/data/hook/shared/pendencies/usePendencyAdvancedFilters';
import { useDynamicParams } from '@/data/hook/useDynamicParams';
import {
  ACTIVITY_PROGRESS_ORDERING_FIELDS,
  ActivityProgressFilterParams,
  ActivityProgressOrdering,
} from '@/data/services/activityProgressServices';
import { ActivityBlockType } from '@/models/Activity';
import { ActivityCorrectionStatusEnum } from '@/models/ActivityProgress';
import { OrderingKey } from '@/models/Filter';
import {
  DateRangeFilterParams,
  parseDateRangeParams,
} from '@/utils/filters/dateRangeParams';
import { createOrderingGuard } from '@/utils/filters/ordering';
import { getNumberParam } from '@/utils/filters/searchParams';

const DEFAULT_ORDERING: ActivityProgressOrdering = '-sent_at';

const isActivityProgressOrdering = createOrderingGuard(
  ACTIVITY_PROGRESS_ORDERING_FIELDS,
);

export type ActivityPendenciesMainFilters = {
  search?: string;
  klassId?: number;
  studentId?: number;
} & DateRangeFilterParams;

type ActivityPendenciesParams = {
  ordering: ActivityProgressOrdering;
  page: number;
} & ActivityPendenciesMainFilters;

export default function useActivityPendenciesFilter() {
  const { params, updateParams } = useDynamicParams<ActivityPendenciesParams>();

  const orderingParam = params.get('ordering');
  const ordering = isActivityProgressOrdering(orderingParam)
    ? orderingParam
    : DEFAULT_ORDERING;

  const {
    advancedFilters,
    advancedFiltersCount,
    teacherFilter,
    unitsFilter,
    updateAdvancedFilters,
    clearAdvancedFilters,
  } = usePendencyAdvancedFilters();

  const mainFilters: ActivityPendenciesMainFilters = {
    search: params.get('search') || undefined,
    klassId: getNumberParam(params, 'klassId'),
    studentId: getNumberParam(params, 'studentId'),
    ...parseDateRangeParams(params),
  };

  const filters: ActivityProgressFilterParams = {
    correctionStatus: [ActivityCorrectionStatusEnum.PENDING],
    activityType: [ActivityBlockType.MANUAL_GRADE],
    isActive: true,
    ordering,
    search: mainFilters.search,
    klassId: mainFilters.klassId ? [mainFilters.klassId] : undefined,
    userId: mainFilters.studentId ? [mainFilters.studentId] : undefined,
    sentAtAfter: mainFilters.dateRangeAfter,
    sentAtBefore: mainFilters.dateRangeBefore,
    teacherId: teacherFilter ? [teacherFilter] : undefined,
    unitId: unitsFilter,
  };

  const toggleOrdering = (field: OrderingKey<ActivityProgressOrdering>) =>
    updateParams({
      ordering: ordering === field ? `-${field}` : field,
      page: FIRST_PAGE,
    });

  const updateMainFilters = (updated: Partial<ActivityPendenciesMainFilters>) =>
    updateParams({ ...updated, page: FIRST_PAGE });

  return {
    filters,
    mainFilters,
    advancedFilters,
    advancedFiltersCount,
    toggleOrdering,
    updateMainFilters,
    updateAdvancedFilters,
    clearAdvancedFilters,
  };
}
